import * as React from 'react';
import {cva, type VariantProps} from 'class-variance-authority';

import {cn} from '@/lib/utils';

/**
 * A real but subordinate action. A soft forest tint: separated from the surface
 * by hue rather than luminance, so it holds on white cards, the grey canvas and
 * stone-50 dialog bodies alike. Shared by `secondary` and its deprecated
 * `outline` alias so the two cannot drift apart.
 *
 * Deliberately carries no `dark:` classes. The webapp's `.dark` theme is not
 * shipped — nothing renders `ThemeToggle`, so it is only ever entered by
 * accident through `prefers-color-scheme` — and the containers are painted
 * light regardless: `ui/dialog.tsx` and `ui/sheet.tsx` have zero `dark:`
 * classes between them, and dialog footers are the commonest home for this
 * tier. A `dark:` fill here would put a near-black chip on a hard-coded white
 * footer, and `.dark .dark\:*` would also out-specify `onDarkSurface` below,
 * disabling the dark-ground escape hatch. Theme the containers first.
 */
const subordinateSurface =
  'bg-primary/10 border-primary/20 text-primary hover:bg-primary/15 active:scale-[0.98] transition-all duration-150';

/**
 * What a button wears on a dark ground — the nav canvas, a selection action bar,
 * the invoice header. `secondary` separates from its background by hue, not
 * luminance, so its forest tint is close to invisible over `bg-nav` or
 * `bg-stone-900`; pass this as `className` there. `ghost` takes
 * `onDarkGhostSurface` instead.
 */
export const onDarkSurface =
  'bg-white/10 border-white/20 text-white hover:bg-white/20';

/** The `ghost` tier's dark-ground counterpart — see `onDarkSurface`. */
export const onDarkGhostSurface =
  'text-white hover:bg-white/10 hover:text-white';

/**
 * What a `secondary` wears on a warm ground — a `Card variant="destructive"`,
 * a red payment-failure band, an amber or orange request card. The forest tint
 * clashes with those hues, so the button lifts off on white instead. Note the
 * hover: `bg-white` alone leaves the variant's `hover:bg-primary/15` in place,
 * so the button sits white and turns green under the cursor. The border goes
 * neutral for the same reason; the forest label stays, because that is what
 * still marks it as the subordinate tier.
 */
export const onTintedSurface =
  'bg-white border-stone-200 hover:bg-stone-50';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-transparent text-sm font-semibold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm active:scale-[0.98] transition-all duration-150',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        secondary: subordinateSurface,
        /** @deprecated — alias of secondary. Do not add new call sites. */
        outline: subordinateSurface,
        ghost: 'hover:bg-stone-100 hover:text-stone-900',
        link: 'text-primary underline-offset-4 hover:underline',
        success: 'bg-success text-success-foreground hover:bg-success/90',
        // Near-black label, not `text-warning-foreground` — white on `--warning`
        // is 3.57:1 and fails AA (RUT-3732).
        warning: 'bg-warning text-stone-950 hover:bg-warning/90',
        action: 'flex-col gap-0.5 h-auto p-1.5 text-foreground border border-transparent hover:text-primary hover:shadow-[0_0_20px_hsl(var(--primary)/0.5)] transition-all duration-200',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
        'icon-sm': 'h-8 w-8 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const buttonClassName = cn(buttonVariants({ variant, size, className }));

    if (asChild && React.isValidElement(children)) {
      // Clone the child element with merged props instead of using Slot
      const child = React.Children.only(children) as React.ReactElement<any>;
      return React.cloneElement(child, {
        ...props,
        ...child.props,
        className: cn(buttonClassName, child.props?.className),
        ref: ref,
      });
    }

    return (
      <button
        className={buttonClassName}
        ref={ref}
        {...props}
      >
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };